import { type RefObject, useRef } from "react";
import type { WorkOrderId } from "../../../lib/workIds";
import { useProcedureAttachmentQuery } from "../../../queries/useProcedureAttachment";
import { useProcedureExecutionQuery } from "../../../queries/useProcedureExecution";
import { ProcedureAttachmentPicker } from "../../work-orders/ProcedureAttachmentPicker";
import {
  type PendingProcedureSave,
  type ProcedureExecutionItem,
  ProcedureExecutionLayout,
} from "../../work-orders/ProcedureExecutionLayout";
import { ProcedurePhotoField } from "../../work-orders/ProcedurePhotoField";
import { canChangeProcedurePhotos } from "../../work-orders/procedurePhotoPermissions";
import { useProcedureItemAutosave } from "../../work-orders/useProcedureItemAutosave";
import { useProcedurePhotoQueue } from "../../work-orders/useProcedurePhotoQueue";
import type { ProcedurePhotoFieldValidation } from "./ProcedureExecutionItemCard";
import {
  executionAnswers,
  mapProcedureExecution,
  savedPhotoAnswers,
} from "./procedureExecution";
import { DetailCard } from "./WorkOrderDetailLayout";

function isUnauthorizedError(error: unknown) {
  return (
    error instanceof Error &&
    (error as Error & { status?: unknown }).status === 403
  );
}

export function ProcedureSection({
  instructions,
  canAttach,
  isEditing = false,
  onProcedureChange,
  selectedProcedureId,
  pendingSaveRef,
  workOrderId,
  workOrderStatusCode,
  workOrderNumber,
  workOrderTitle,
}: {
  instructions: string | null;
  canAttach: boolean;
  isEditing?: boolean;
  onProcedureChange?: (procedureId: string | null) => void;
  selectedProcedureId?: string | null;
  /** Receives a function that flushes unsaved answers before completion. */
  pendingSaveRef?: RefObject<PendingProcedureSave | null>;
  workOrderId: WorkOrderId;
  workOrderStatusCode?: string;
  workOrderNumber?: string;
  workOrderTitle?: string;
}) {
  const attachmentQuery = useProcedureAttachmentQuery(workOrderId);
  const attachment = attachmentQuery.data ?? null;
  const executionQuery = useProcedureExecutionQuery(
    workOrderId,
    Boolean(attachment),
  );
  const itemSave = useProcedureItemAutosave(workOrderId);
  const photoQueue = useProcedurePhotoQueue({ workOrderId, paused: false });
  const lastImageRefresh = useRef(0);
  const execution = executionQuery.data;
  // Done and Canceled Work Orders are read-only, so the Procedure locks too.
  // The Work Order status is authoritative when known: the cached execution
  // status can lag behind a reopen, so it is only a fallback.
  const readOnly =
    workOrderStatusCode === undefined
      ? execution?.status === "COMPLETED"
      : workOrderStatusCode === "COMPLETED" ||
        workOrderStatusCode === "CANCELED";
  const canChangePhotos = canChangeProcedurePhotos({
    workOrderStatusCode,
    executionStatus:
      workOrderStatusCode === undefined ? execution?.status : undefined,
    readOnly,
  });
  const photoIds = savedPhotoAnswers(execution);
  function refreshExpiredImages() {
    // A thumbnail fails to load once its signed URL expires; reload the
    // execution for fresh URLs, at most once a minute.
    if (Date.now() - lastImageRefresh.current < 60_000) return;
    lastImageRefresh.current = Date.now();
    void executionQuery.refetch();
  }
  function photoField(
    item: ProcedureExecutionItem,
    validation: ProcedurePhotoFieldValidation,
  ) {
    const response = execution?.responses.find(
      (candidate) => candidate.itemId === item.id,
    );
    const savedFileIds = photoIds[item.id] ?? [];
    return (
      <ProcedurePhotoField
        canChange={canChangePhotos}
        entries={photoQueue.entriesFor(item.id)}
        error={validation.error}
        errorDescriptionId={validation.errorDescriptionId}
        itemLabel={item.label}
        onDiscard={photoQueue.discard}
        onFilesSelected={(files) =>
          photoQueue.addFiles(item.id, files, savedFileIds.length)
        }
        onImageError={refreshExpiredImages}
        onRemoveSaved={(fileId) => photoQueue.removeSaved(item.id, fileId)}
        onRetry={photoQueue.retry}
        removingIds={photoQueue.removingIds}
        savedFileIds={savedFileIds}
        savedPhotos={response?.photos ?? []}
        selectionError={photoQueue.selectionErrorFor(item.id)}
      />
    );
  }
  const procedure = executionQuery.data?.procedure
    ? mapProcedureExecution(
        executionQuery.data.procedure,
        executionQuery.data.status,
      )
    : undefined;
  const hasAttachment = attachment !== null;
  const showExecution =
    attachmentQuery.isPending || attachmentQuery.isError || hasAttachment;
  const state = attachmentQuery.isPending
    ? "loading"
    : isUnauthorizedError(attachmentQuery.error) ||
        (hasAttachment && isUnauthorizedError(executionQuery.error))
      ? "unauthorized"
      : attachmentQuery.isError ||
          (hasAttachment &&
            executionQuery.isError &&
            executionQuery.data === undefined) ||
          (hasAttachment &&
            !procedure &&
            !executionQuery.isPending &&
            executionQuery.data === undefined)
        ? "error"
        : hasAttachment
          ? "ready"
          : "unavailable";
  return (
    <DetailCard id="procedure" title="Procedure">
      <div className="px-(--spacing-20) py-(--spacing-16)">
        <ProcedureAttachmentPicker
          attachment={attachment}
          canAttach={canAttach}
          isEditing={isEditing}
          loading={attachmentQuery.isPending}
          onProcedureChange={onProcedureChange}
          selectedProcedureId={selectedProcedureId}
          workOrderId={workOrderId}
        />
        {showExecution ? (
          <ProcedureExecutionLayout
            initialAnswers={executionAnswers(executionQuery.data)}
            instructions={instructions}
            itemSave={procedure && executionQuery.data ? itemSave : undefined}
            onRetry={() => {
              void attachmentQuery.refetch();
              if (attachment) void executionQuery.refetch();
            }}
            pendingSaveRef={pendingSaveRef}
            photoField={photoField}
            procedure={procedure}
            readOnly={readOnly}
            savedPhotoAnswers={photoIds}
            state={state}
            workOrderNumber={workOrderNumber}
            workOrderTitle={workOrderTitle}
          />
        ) : null}
      </div>
    </DetailCard>
  );
}
